import { PatientRecord } from "@common/utils/types";
import type { ReviewSessionState } from "./reviewSessionSlice";

// EN-18: the review survives a refresh of this tab. sessionStorage, not localStorage: it is per tab and gone when
// the tab closes, so a review never reappears in another tab or a later visit to the app.
export const REVIEW_SESSION_STORAGE_KEY = "hd.reviewSession.v1";

type StoredReviewSession = Omit<ReviewSessionState, "active" | "moving" | "rowScrollLeft">;

// Only what the cards show. Notes, transcripts and documents are never written to storage: the open visit and
// the visit list supply the live record.
const cardFields = (record: PatientRecord): PatientRecord =>
    ({
        id: record.id,
        stage: record.stage,
        scheduled_at: record.scheduled_at,
        patient_name: record.patient_name,
        patients: record.patients ? { name: record.patients.name } : undefined,
        doctors: record.doctors ? { doctor_name: record.doctors.doctor_name } : undefined,
    }) as PatientRecord;

export function saveReviewSession(state: ReviewSessionState): void {
    try {
        if (!state.active) {
            sessionStorage.removeItem(REVIEW_SESSION_STORAGE_KEY);
            return;
        }
        const stored: StoredReviewSession = {
            records: state.records.map(cardFields),
            currentIndex: state.currentIndex,
            skippedIds: state.skippedIds,
            accountId: state.accountId,
            userId: state.userId,
            entered: state.entered,
            startPath: state.startPath,
        };
        sessionStorage.setItem(REVIEW_SESSION_STORAGE_KEY, JSON.stringify(stored));
    } catch {
        // Storage blocked or full: the review still works, it just won't survive a refresh.
    }
}

const isStored = (value: unknown): value is StoredReviewSession => {
    const v = value as StoredReviewSession | null;
    return (
        !!v &&
        Array.isArray(v.records) &&
        v.records.length > 0 &&
        v.records.every((r) => typeof r?.id === "string") &&
        Number.isInteger(v.currentIndex) &&
        v.currentIndex >= 0 &&
        v.currentIndex < v.records.length &&
        Array.isArray(v.skippedIds) &&
        typeof v.userId === "string" &&
        (v.accountId === null || typeof v.accountId === "string")
    );
};

/** The review saved in this tab, or undefined when there is none or it can't be read. */
export function restoreReviewSession(): ReviewSessionState | undefined {
    try {
        const raw = sessionStorage.getItem(REVIEW_SESSION_STORAGE_KEY);
        if (!raw) return undefined;
        const stored: unknown = JSON.parse(raw);
        if (!isStored(stored)) {
            sessionStorage.removeItem(REVIEW_SESSION_STORAGE_KEY);
            return undefined;
        }
        // A move in flight when the page reloaded never finished; the restored bar starts idle.
        return { ...stored, active: true, moving: false, rowScrollLeft: 0 };
    } catch {
        return undefined;
    }
}
